import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Award, Download, Link2, Linkedin, Twitter, Facebook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CertificatePage() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/certificates/${certId}`)
      .then((r) => setCert(r.data))
      .catch(() => setMissing(true));
  }, [certId]);

  const shareUrl = window.location.href;
  const text = cert
    ? `I just completed ${cert.course_title} from SkillPreneur Academy!`
    : "";

  const links = cert
    ? [
        {
          id: "linkedin",
          label: "LinkedIn",
          icon: Linkedin,
          href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
        },
        {
          id: "twitter",
          label: "X / Twitter",
          icon: Twitter,
          href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
        },
        {
          id: "facebook",
          label: "Facebook",
          icon: Facebook,
          href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
        },
      ]
    : [];

  if (missing) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0F172A] text-white px-6">
        <div data-testid="certificate-not-found" className="text-center">
          <h1 className="font-display text-2xl font-semibold tracking-tight">Certificate not found</h1>
          <p className="mt-2 text-sm text-slate-400">
            Please check the certificate ID and try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] text-white px-5 py-16">
      <div data-testid="public-certificate" className="mx-auto max-w-2xl">
        <div className="rounded-3xl border border-white/10 bg-white/[0.04] overflow-hidden">
          <div className="p-8 sm:p-10 border-b border-white/10">
            <div className="flex items-center gap-2 text-[#06B6D4]">
              <Award className="h-4 w-4" />
              <span className="text-[10px] uppercase tracking-[0.28em] font-semibold">
                Certificate of completion
              </span>
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tighter">
              {cert?.course_title || "…"}
            </h1>
            <p className="mt-3 text-base text-slate-300">
              Awarded to <span className="font-semibold text-white">{cert?.student_name}</span>
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-xs text-slate-400">
              <div>
                Certificate ID
                <div className="mt-0.5 text-white font-medium">{cert?.cert_id}</div>
              </div>
              <div>
                Issued on
                <div className="mt-0.5 text-white font-medium">
                  {cert?.issued_at ? new Date(cert.issued_at).toLocaleDateString() : "—"}
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-3">
            <a
              href={`${API}/certificates/${certId}/download`}
              data-testid="public-cert-download"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0F172A] px-5 py-3 text-xs font-semibold hover:bg-slate-100 transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Download PDF
            </a>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`public-cert-share-${l.id}`}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/15 px-3 py-2.5 text-[11px] hover:border-[#06B6D4]/60 transition-colors"
                >
                  <l.icon className="h-3.5 w-3.5" /> {l.label}
                </a>
              ))}
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  toast.success("Link copied");
                }}
                data-testid="public-cert-copy"
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/15 px-3 py-2.5 text-[11px] hover:border-[#06B6D4]/60 transition-colors"
              >
                <Link2 className="h-3.5 w-3.5" /> Copy link
              </button>
            </div>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">
          Verified credential issued by SkillPreneur Academy.
        </p>
      </div>
    </div>
  );
}
